import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { DoubtStatusBadge, DoubtVisibilityBadge, DOUBT_STATUS_FILTERS } from "@/lib/doubts";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircleQuestion, MessageSquare } from "lucide-react";

// Shared by ADMIN (/admin/doubts, all batches) and FACULTY (same route, the
// backend scopes the list + the batch filter to their assigned batches) —
// mirrors the existing AttendanceReport reuse pattern.
export default function Doubts() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [batches, setBatches] = useState([]);
    const [courses, setCourses] = useState([]);
    const [batchFilter, setBatchFilter] = useState("");
    const [courseFilter, setCourseFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");

    useEffect(() => {
        api.get("/batches").then(r => setBatches(r.data.items)).catch(() => {});
        api.get("/courses").then(r => setCourses(r.data.items)).catch(() => {});
    }, []);

    const load = async () => {
        const params = new URLSearchParams();
        if (batchFilter) params.append("batch_id", batchFilter);
        if (courseFilter) params.append("course_id", courseFilter);
        if (statusFilter !== "ALL") params.append("status", statusFilter);
        const { data } = await api.get(`/doubts?${params}`);
        setItems(data.items);
    };
    useEffect(() => { load(); }, [batchFilter, courseFilter, statusFilter]); // eslint-disable-line

    const batchName = (id) => batches.find(b => b.id === id)?.name || id;
    const counts = items.reduce((acc, d) => { acc[d.status] = (acc[d.status] || 0) + 1; return acc; }, {});

    return (
        <div className="space-y-6" data-testid="staff-doubts-page">
            <div>
                <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Q&amp;A queue</div>
                <h1 className="font-display text-3xl lg:text-4xl mt-1">Doubts</h1>
                <p className="text-sm text-muted-foreground mt-1">Unanswered doubts first. Any of you can answer any doubt on your batches.</p>
            </div>

            <div className="flex flex-wrap gap-3 items-center">
                <Select value={batchFilter || "ALL"} onValueChange={(v) => setBatchFilter(v === "ALL" ? "" : v)}>
                    <SelectTrigger className="w-48" data-testid="doubts-batch-filter"><SelectValue placeholder="All batches" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">All batches</SelectItem>
                        {batches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={courseFilter || "ALL"} onValueChange={(v) => setCourseFilter(v === "ALL" ? "" : v)}>
                    <SelectTrigger className="w-48" data-testid="doubts-course-filter"><SelectValue placeholder="All courses" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">All courses</SelectItem>
                        {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-44" data-testid="doubts-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOUBT_STATUS_FILTERS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
                <Badge className="bg-[#E64AA1] hover:bg-[#E64AA1] text-white">{counts.open || 0} open</Badge>
                <Badge className="bg-[#FFA81B] hover:bg-[#FFA81B] text-white">{counts.answered || 0} answered</Badge>
                <Badge className="bg-[#16A34A] hover:bg-[#16A34A] text-white">{counts.resolved || 0} resolved</Badge>
            </div>

            <Card className="mx-card">
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Doubt</TableHead>
                                <TableHead>Student</TableHead>
                                <TableHead>Course</TableHead>
                                <TableHead>Batch</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Visibility</TableHead>
                                <TableHead>Replies</TableHead>
                                <TableHead>Posted</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map(d => (
                                <TableRow key={d.id} className="cursor-pointer" onClick={() => navigate(`/admin/doubts/${d.id}`)} data-testid={`doubt-row-${d.id}`}>
                                    <TableCell className="max-w-xs">
                                        <div className="font-semibold text-sm truncate">{d.title}</div>
                                        <div className="text-xs text-muted-foreground truncate">{d.body}</div>
                                    </TableCell>
                                    <TableCell className="text-sm">{d.student_name}</TableCell>
                                    <TableCell className="text-sm">{d.course_title}</TableCell>
                                    <TableCell className="text-sm">{batchName(d.batch_id)}</TableCell>
                                    <TableCell><DoubtStatusBadge status={d.status} /></TableCell>
                                    <TableCell><DoubtVisibilityBadge visibility={d.visibility} /></TableCell>
                                    <TableCell className="text-sm"><MessageSquare className="h-3.5 w-3.5 inline mr-1 text-muted-foreground" />{d.reply_count}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{new Date(d.created_at).toLocaleDateString()}</TableCell>
                                </TableRow>
                            ))}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center text-muted-foreground py-12">
                                        <MessageCircleQuestion className="h-10 w-10 mx-auto mb-3 opacity-40" />
                                        No doubts match these filters.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
